// src/scripts/contact/index.js

/**
 * Main entry point for the Contact System
 *
 * This module coordinates initialization, exposes the public API,
 * and wires up diagnostics & lifecycle management.
 * 
 * RESTORED: Full ContactFixedForm support enabled
 */

let moduleExports = {};

try {
  // ── CORE CONSTANTS & STATE ───────────────────────────────────────────────────
  const { isBrowser, CONTACT_LOADED_KEY } = await import(
    './internal/constants.js'
  );
  const {
    getContactSystem,
    getContactSystemSync,
    withContactSystem,
    reinitializeContactSystem,
    debugContactSystem
  } = await import('./api.js');

  // ── PARALLEL UTILITIES ───────────────────────────────────────────────────────
  const [ diagnostics, ManagerModule, ContactFixedFormModule ] = await Promise.all([
    import('./internal/diagnostics.js'),
    import('./internal/ContactSystemManager.js'),
    import('./core/ContactFixedForm.js') // RESTORED: Import ContactFixedForm
  ]);

  const { environment, getInternalState } = diagnostics; // Environment detection and diagnostics
  const ContactSystemManager = ManagerModule.default;   // ContactSystemManager is the main class handling lifecycle
  const ContactFixedForm = ContactFixedFormModule.default; // RESTORED: Fixed form component

  // ── PUBLIC API ───────────────────────────────────────────────────────────────
  moduleExports.getContactSystem          = getContactSystem;
  moduleExports.getContactSystemSync      = getContactSystemSync;
  moduleExports.withContactSystem         = withContactSystem;
  moduleExports.reinitializeContactSystem = reinitializeContactSystem;
  moduleExports.debugContactSystem        = debugContactSystem;

  // ── RESTORED: FIXED FORM API ─────────────────────────────────────────────────
  moduleExports.ContactFixedForm = ContactFixedForm;
  
  // Helper function to create fixed forms with the contact namespace
  moduleExports.createFixedForm = async (options = {}) => {
    try {
      const namespace = await getContactSystem();
      const fixedForm = ContactFixedForm.create(namespace, options);
      console.log('✅ Fixed form created successfully:', {
        formSelector: options.formSelector || '#contact-form-fixed',
        timestamp: new Date().toISOString()
      });
      return fixedForm;
    } catch (error) {
      console.error('❌ Failed to create fixed form:', error);
      throw error;
    }
  };

  // Helper function to create multiple fixed forms
  moduleExports.createMultipleFixedForms = async (formsConfig = []) => {
    try {
      const namespace = await getContactSystem();
      const forms = {};
      
      for (const config of formsConfig) {
        const { name, ...options } = config;
        if (!name) {
          console.warn('⚠️ Skipping form config without name:', config);
          continue;
        }
        forms[name] = ContactFixedForm.create(namespace, options);
        console.log(`✅ Created fixed form: ${name}`);
      }
      
      console.log(`✅ Created ${Object.keys(forms).length} fixed forms`);
      return forms;
    } catch (error) {
      console.error('❌ Failed to create multiple fixed forms:', error);
      throw error;
    }
  };

  // ── DIAGNOSTICS ──────────────────────────────────────────────────────────────
  moduleExports.environment      = environment;
  moduleExports.getInternalState = getInternalState;

  // ── LIFECYCLE ────────────────────────────────────────────────────────────────
  moduleExports.cleanup = ContactSystemManager.cleanup;
  moduleExports.recover = ContactSystemManager.recover;

  // ── AUTO‑BOOTSTRAP ────────────────────────────────────────────────────────────
  moduleExports.default = await ContactSystemManager.init();

  console.log(`✅ Contact system loaded with full fixed form support (${environment})`, {
    version:   '1.0.0',
    timestamp: new Date().toISOString(),
    fixedFormSupport: true,
    contactFixedFormAvailable: !!ContactFixedForm
  });

} catch (err) {
  console.error('💥 Fatal error loading ContactSystem:', err);
  console.error('Error details:', {
    message: err.message,
    stack: err.stack,
    cause: err.cause
  });
  
  moduleExports = {
    getContactSystem:          () => Promise.reject(err),
    getContactSystemSync:      () => null,
    withContactSystem:         () => Promise.reject(err),
    reinitializeContactSystem: () => Promise.reject(err),
    debugContactSystem:        () => {},
    ContactFixedForm:          null,
    createFixedForm:           () => Promise.reject(err),
    createMultipleFixedForms:  () => Promise.reject(err),
    environment:               'error',
    getInternalState:          () => ({
      isInitialized: false,
      hasInstance:   false,
      hasPromise:    false,
      debugModeEnabled: false,
      globallyLoaded:   false,
      environment:      'error',
      moduleVersion:    '1.0.0',
      fixedFormSupport: false,
      error:            err.message
    }),
    cleanup:  () => Promise.resolve(),
    recover:  () => Promise.reject(new Error('Recovery unavailable')),
    default:  Promise.reject(err)
  };
}

// ── EXPORTS ───────────────────────────────────────────────────────────────────
export const getContactSystem          = moduleExports.getContactSystem;
export const getContactSystemSync      = moduleExports.getContactSystemSync;
export const withContactSystem         = moduleExports.withContactSystem;
export const reinitializeContactSystem = moduleExports.reinitializeContactSystem;
export const debugContactSystem        = moduleExports.debugContactSystem;

// RESTORED: Fixed form exports
export const ContactFixedForm          = moduleExports.ContactFixedForm;
export const createFixedForm           = moduleExports.createFixedForm;
export const createMultipleFixedForms  = moduleExports.createMultipleFixedForms;

export const environment               = moduleExports.environment;
export const getInternalState          = moduleExports.getInternalState;
export const cleanup                   = moduleExports.cleanup;
export const recover                   = moduleExports.recover;
export default moduleExports.default;